import { DataSource } from '@angular/cdk/table';
import { Component, Input } from '@angular/core';
import { UntypedFormArray, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import {
  CARER_ROLE,
  COORDINATOR_ROLE,
  FAMILY_ROLE,
  ORGANISATION_ADMIN_ROLE,
  ORGANISATION_ROLES,
} from '@careapp-group/core';
import * as Papa from 'papaparse';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PreviewColumnConfig {
  property: string;
  header: string;
  unique: boolean;
  default: any;
  role: boolean;
  isBoolean: boolean;
}

@Component({
    selector: 'member-import-csv-preview',
    templateUrl: 'member-import-csv-preview.component.html',
    styleUrls: ['member-import-csv-preview.component.scss'],
    standalone: false
})
export class MemberImportCsvPreviewComponent {
  firstName = 'firstName';
  lastName = 'lastName';
  email = 'email';
  accessAllCustomers = 'accessAllCustomers';
  externalId = 'externalId';
  role = 'role';
  nonAdminRoles = [CARER_ROLE, COORDINATOR_ROLE];
  organisationRoles = ORGANISATION_ROLES;
  protected _file: any;
  displayedColumns: BehaviorSubject<string[]> = new BehaviorSubject([]);
  dataSource: CsvDataSource;
  rows: UntypedFormArray;
  data = new BehaviorSubject([]);
  form = new UntypedFormGroup({});

  get valid() {
    return this.form.valid;
  }
  get value() {
    return this.form.value;
  }
  @Input() config: PreviewColumnConfig[];
  @Input()
  set results(value: any[]) {
    this._results = value;
    if (value) {
      this.form.disable();
    }
  }
  get results() {
    return this._results;
  }
  @Input() error: any;
  @Input()
  set loading(loading: boolean) {
    if (loading) {
      this.form.disable();
    } else if (!this.results) {
      this.form.enable();
    }
    this._loading = loading;
  }
  get loading() {
    return this._loading;
  }

  @Input()
  set file(value) {
    if (value) {
      Papa.parse(value, {
        header: true,
        complete: (results) => {
          this.initTable(results);
        },
        skipEmptyLines: true,
      });
    } else {
      this.initTable(null);
    }
  }

  private _loading: boolean;
  private _results: any[];

  parseRole(role: string) {
    switch (role.trim().toLowerCase()) {
      case 'admin':
      case 'administrator':
      case 'org admin':
      case 'orgadmin':
      case 'organisation administrator':
        return ORGANISATION_ADMIN_ROLE;
      case 'coordinator':
      case 'coord':
        return COORDINATOR_ROLE;
      case 'team member':
      case 'carer':
        return CARER_ROLE;
      case 'family':
      case 'family member':
      case 'familymember':
        return FAMILY_ROLE;
      default:
        return CARER_ROLE;
    }
  }

  initTable(result) {
    this.displayedColumns.next(this.config.map((col) => col.property).concat(['action', 'result']));

    // Filter out rows that have duplicates of unique keys
    this.config.forEach((option) => {
      const existing = new Set();
      if (option.unique) {
        result.data = result.data.filter((item) => {
          if (existing.has(item[option.property])) {
            return false;
          }
          existing.add(item[option.property]);
          return true;
        });
      }
      if (option.default) {
        result.data.forEach(
          (item) => (item[option.property] = item[option.property] || option.default)
        );
      }
    });

    // If a non-null value appears in accessAllCustomers, parse it to a boolean.
    result.data.forEach((item) => {
      let parsedBool;
      if (item[this.accessAllCustomers] != null) {
        try {
          parsedBool = Boolean(JSON.parse(item[this.accessAllCustomers]));
        } catch (err) {
          parsedBool = (item[this.accessAllCustomers]?.toLowerCase() ?? '') === 'true';
        }
      }
      item[this.accessAllCustomers] = parsedBool;
    });

    this.rows = new UntypedFormArray(
      result.data.map((row) => {
        // Value has a default, so won't be null.
        const isCustomRole = !this.organisationRoles.some((orgRole) => orgRole === row[this.role]);
        if (isCustomRole) {
          row[this.role] = this.parseRole(row[this.role]);
        }

        // AccessAllCustomers default if null is Coordinators: True, carers: false
        if (!row[this.accessAllCustomers]) {
          row[this.accessAllCustomers] = row.role === COORDINATOR_ROLE;
        }

        // Trim whitespace on email and external ID.

        if (!!row[this.firstName]) {
          row[this.firstName] = row[this.firstName].trim();
        }
        if (!!row[this.lastName]) {
          row[this.lastName] = row[this.lastName].trim();
        }
        if (!!row[this.email]) {
          row[this.email] = row[this.email].trim();
        }
        if (!!row[this.externalId]) {
          row[this.externalId] = row[this.externalId].trim();
        }

        const fg = new UntypedFormGroup({});
        this.config.forEach((option) => {
          fg.addControl(option.property, new UntypedFormControl(row[option.property]));
        });
        return fg;
      }),
      Validators.minLength(1)
    );

    this.form.setControl('rows', this.rows);
    this.data.next(result.data);
    this.dataSource = new CsvDataSource(this.data.asObservable());
  }

  controlForCell(index: number, property: string) {
    try {
      const item: any = this.rows.controls[index];
      return item.controls[property];
    } catch (ex) {
      return new UntypedFormControl('');
    }
  }

  roleForCell(index: number) {
    try {
      return this.form.value.rows[index].role ?? this.data.value[index].role;
    } catch (error) {
      return '';
    }
  }

  hasAccessAllCustomersProperty(index: number) {
    return !this.nonAdminRoles.includes(this.roleForCell(index));
  }

  removeAt(idx: number) {
    this.rows.removeAt(idx);
    const items = this.data.value;
    this.data.next(items.slice(0, idx).concat(items.slice(idx + 1)));
  }

  flattenError(result: any) {
    if (result.errors) {
      const errors = result.errors;
      try {
        for (const key in errors) {
          if (Array.isArray(errors[key])) {
            return `Failed (${this.titleCase(key)}: ${errors[key][0]})`;
          }
        }
      } catch (ex) {}
    }
    return 'Import failed.';
  }

  titleCase(value: string) {
    return (
      value[0].toUpperCase() +
      value
        .replace(/([A-Z])/g, ' $1')
        .toLowerCase()
        .slice(1)
    );
  }
}

export class CsvDataSource extends DataSource<any> {
  constructor(public data: Observable<any[]>) {
    super();
  }

  connect(): Observable<any[]> {
    return this.data;
  }

  disconnect() {
    // lint
  }
}
